(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-fs-motion]';

  function clamp01(value) {
    return Math.max(0, Math.min(1, value));
  }

  function windowOpacity(progress, start, end, fade) {
    if (progress < start || progress > end) return 0;
    return Math.max(0, Math.min(1, (progress - start) / fade, (end - progress) / fade));
  }

  function flashAt(progress, point) {
    return Math.max(0, 1 - Math.abs(progress - point) / 0.012);
  }

  function initMotion(root) {
    if (!root || root.dataset.fsMotionInitialized === 'true') return;

    var film = root.querySelector('[data-fs-film]');
    var sticky = root.querySelector('[data-fs-film-sticky]');
    if (!film || !sticky) return;

    root.dataset.fsMotionInitialized = 'true';
    root.classList.add('is-motion-ready');

    var scenes = Array.prototype.slice.call(root.querySelectorAll('[data-fs-scene]'));
    var cutLabels = Array.prototype.slice.call(root.querySelectorAll('[data-fs-cut]'));
    var sceneNumber = root.querySelector('[data-fs-scene-number]');
    var filmTime = root.querySelector('[data-fs-film-time]');
    var progressText = root.querySelector('[data-fs-progress]');
    var system = root.querySelector('[data-fs-system]');
    var revealItems = Array.prototype.slice.call(root.querySelectorAll('[data-fs-reveal]'));
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var finePointer = window.matchMedia('(pointer: fine)').matches;

    var targetProgress = 0;
    var displayedProgress = 0;
    var frame = 0;
    var pointerFrame = 0;
    var scrollEndTimer = 0;
    var filmIsNear = true;
    var systemIsNear = false;
    var lastCompositeGroup = '';
    var lastScene = 0;
    var lastPercent = -1;
    var lastSecond = -1;
    var pointerX = 0;
    var pointerY = 0;
    var filmStartY = 0;
    var filmHeight = 1;
    var filmDistance = 1;
    var lastFrameTime = 0;

    function loadScene(index) {
      var picture = scenes[index];
      if (!picture) return;

      var image = picture.querySelector('[data-fs-deferred-scene]');
      if (!image) return;

      var source = picture.querySelector('source[data-fs-srcset]');
      if (source && source.dataset.fsSrcset) {
        source.srcset = source.dataset.fsSrcset;
        source.removeAttribute('data-fs-srcset');
      }

      if (image.dataset.fsSrc) {
        image.src = image.dataset.fsSrc;
        image.removeAttribute('data-fs-src');
      }
      image.loading = 'eager';
      image.removeAttribute('data-fs-deferred-scene');
    }

    function loadScenesForProgress(progress) {
      if (progress >= 0.02) loadScene(1);
      if (progress >= 0.22) loadScene(2);
      if (progress >= 0.42) loadScene(3);
      if (progress >= 0.62) loadScene(4);
    }

    function measureFilm() {
      var rect = film.getBoundingClientRect();
      filmStartY = window.scrollY + rect.top;
      filmHeight = film.offsetHeight;
      filmDistance = Math.max(filmHeight - window.innerHeight, 1);
    }

    function readFilmProgress() {
      return clamp01((window.scrollY - filmStartY) / filmDistance);
    }

    function updateSystemProgress() {
      if (!system || !systemIsNear) return;
      var rect = system.getBoundingClientRect();
      var progress = clamp01((window.innerHeight - rect.top) / (window.innerHeight + rect.height));
      system.style.setProperty('--fs-system-scale', (1.07 - progress * 0.06).toFixed(4));
      system.style.setProperty('--fs-system-y', ((progress - 0.5) * -5).toFixed(3) + '%');
    }

    function applyProgress(progress) {
      loadScenesForProgress(progress);
      var cutOne = clamp01((progress - 0.12) / 0.065);
      var cutTwo = clamp01((progress - 0.32) / 0.065);
      var cutThree = clamp01((progress - 0.52) / 0.065);
      var cutFour = clamp01((progress - 0.72) / 0.065);
      var flash = Math.max(
        flashAt(progress, 0.15),
        flashAt(progress, 0.35),
        flashAt(progress, 0.55),
        flashAt(progress, 0.75)
      );
      var scene = progress < 0.13 ? 1 : progress < 0.33 ? 2 : progress < 0.53 ? 3 : progress < 0.73 ? 4 : 5;
      var compositeGroup = progress < 0.205 ? '0,1' : progress < 0.405 ? '1,2' : progress < 0.605 ? '2,3' : progress < 0.805 ? '3,4' : '4';
      var introOpacity = Math.max(0, 1 - progress * 6.8);
      var extremeOpacity = windowOpacity(progress, 0.17, 0.35, 0.05);
      var fishingOpacity = windowOpacity(progress, 0.37, 0.55, 0.05);
      var ascentOpacity = windowOpacity(progress, 0.57, 0.75, 0.05);
      var rideOpacity = clamp01((progress - 0.79) / 0.07);

      sticky.style.setProperty('--fs-film-progress', progress.toFixed(4));
      sticky.style.setProperty('--fs-scene-one-scale', (1.02 + Math.min(progress, 0.20) * 0.30).toFixed(4));
      sticky.style.setProperty('--fs-scene-one-x', (progress * -1.4).toFixed(3) + '%');
      sticky.style.setProperty('--fs-scene-one-y', (progress * -1).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-one-right', (100 - cutOne * 100).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-one-scale', (1.16 - cutOne * 0.10).toFixed(4));
      sticky.style.setProperty('--fs-cut-one-x', ((1 - cutOne) * 5).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-two-top', (100 - cutTwo * 100).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-two-scale', (1.15 - cutTwo * 0.10).toFixed(4));
      sticky.style.setProperty('--fs-cut-two-rotate', ((1 - cutTwo) * -0.8).toFixed(3) + 'deg');
      sticky.style.setProperty('--fs-cut-three-leading', (cutThree * 112).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-three-trailing', Math.max(0, cutThree * 112 - 12).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-three-scale', (1.13 - cutThree * 0.08).toFixed(4));
      sticky.style.setProperty('--fs-cut-three-x', ((1 - cutThree) * 4).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-four-left', (100 - cutFour * 100).toFixed(3) + '%');
      sticky.style.setProperty('--fs-cut-four-scale', (1.08 - cutFour * 0.08).toFixed(4));
      sticky.style.setProperty('--fs-cut-four-x', ((1 - cutFour) * 3).toFixed(3) + '%');
      sticky.style.setProperty('--fs-flash-opacity', flash.toFixed(4));
      sticky.style.setProperty('--fs-intro-opacity', introOpacity.toFixed(4));
      sticky.style.setProperty('--fs-ascent-opacity', ascentOpacity.toFixed(4));
      sticky.style.setProperty('--fs-extreme-opacity', extremeOpacity.toFixed(4));
      sticky.style.setProperty('--fs-fishing-opacity', fishingOpacity.toFixed(4));
      sticky.style.setProperty('--fs-ride-opacity', rideOpacity.toFixed(4));
      sticky.style.setProperty('--fs-intro-y', ((1 - introOpacity) * -18).toFixed(2) + 'px');
      sticky.style.setProperty('--fs-ascent-y', ((1 - ascentOpacity) * 22).toFixed(2) + 'px');
      sticky.style.setProperty('--fs-extreme-y', ((1 - extremeOpacity) * 22).toFixed(2) + 'px');
      sticky.style.setProperty('--fs-fishing-y', ((1 - fishingOpacity) * 22).toFixed(2) + 'px');
      sticky.style.setProperty('--fs-ride-y', ((1 - rideOpacity) * 22).toFixed(2) + 'px');
      sticky.style.setProperty('--fs-meter-scale', Math.max(progress, 0.04).toFixed(4));
      sticky.style.setProperty('--fs-rail-x', (16 - progress * 76).toFixed(3) + '%');

      if (compositeGroup !== lastCompositeGroup) {
        var visible = compositeGroup.split(',').map(Number);
        scenes.forEach(function (item, index) {
          item.classList.toggle('is-composited', visible.indexOf(index) !== -1);
        });
        lastCompositeGroup = compositeGroup;
      }

      if (scene !== lastScene) {
        if (sceneNumber) sceneNumber.textContent = '0' + scene;
        cutLabels.forEach(function (item, index) {
          item.classList.toggle('is-active', scene === index + 1);
        });
        lastScene = scene;
      }

      var percent = Math.round(progress * 100);
      var second = Math.round(progress * 15);
      if (percent !== lastPercent && progressText) {
        progressText.textContent = String(percent).padStart(2, '0');
        lastPercent = percent;
      }
      if (second !== lastSecond && filmTime) {
        filmTime.textContent = '00:' + String(second).padStart(2, '0') + ':00';
        lastSecond = second;
      }

      var viewportTop = window.scrollY;
      root.classList.toggle(
        'is-film-active',
        viewportTop + window.innerHeight > filmStartY && viewportTop < filmStartY + filmHeight
      );
    }

    function animateProgress(timestamp) {
      var elapsed = lastFrameTime ? Math.min(timestamp - lastFrameTime, 34) : 16.7;
      var smoothing = reducedMotion ? 1 : 1 - Math.exp(-elapsed / 62);
      lastFrameTime = timestamp;
      displayedProgress += (targetProgress - displayedProgress) * smoothing;
      if (Math.abs(targetProgress - displayedProgress) < 0.0002) displayedProgress = targetProgress;

      applyProgress(displayedProgress);
      updateSystemProgress();

      if (displayedProgress !== targetProgress) {
        frame = window.requestAnimationFrame(animateProgress);
      } else {
        frame = 0;
        lastFrameTime = 0;
      }
    }

    function updateTargets() {
      var viewportTop = window.scrollY;
      filmIsNear = viewportTop + window.innerHeight * 2 > filmStartY && viewportTop < filmStartY + filmHeight + window.innerHeight;
      targetProgress = readFilmProgress();
      updateSystemProgress();

      root.classList.add('is-scrolling');
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = window.setTimeout(function () {
        root.classList.remove('is-scrolling');
      }, 160);

      if (!frame) frame = window.requestAnimationFrame(animateProgress);
    }

    function handleResize() {
      measureFilm();
      updateTargets();
    }

    function handlePointer(event) {
      if (!finePointer || root.classList.contains('is-scrolling') || !filmIsNear) return;
      pointerX = event.clientX / window.innerWidth - 0.5;
      pointerY = event.clientY / window.innerHeight - 0.5;
      if (pointerFrame) return;
      pointerFrame = window.requestAnimationFrame(function () {
        root.style.setProperty('--fs-stage-x', (pointerX * -5).toFixed(2) + 'px');
        root.style.setProperty('--fs-stage-y', (pointerY * -3).toFixed(2) + 'px');
        root.style.setProperty('--fs-ice-a-x', (pointerX * 22).toFixed(2) + 'px');
        root.style.setProperty('--fs-ice-a-y', (pointerY * 16).toFixed(2) + 'px');
        root.style.setProperty('--fs-ice-b-x', (pointerX * -18).toFixed(2) + 'px');
        root.style.setProperty('--fs-ice-b-y', (pointerY * -12).toFixed(2) + 'px');
        pointerFrame = 0;
      });
    }

    var systemObserver = null;
    var revealObserver = null;

    if ('IntersectionObserver' in window) {
      if (system) {
        systemObserver = new IntersectionObserver(function (entries) {
          systemIsNear = entries[0].isIntersecting;
          if (systemIsNear) updateSystemProgress();
        }, { rootMargin: '60% 0px 60% 0px' });
        systemObserver.observe(system);
      }

      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
      revealItems.forEach(function (item) { revealObserver.observe(item); });
    } else {
      systemIsNear = true;
      revealItems.forEach(function (item) { item.classList.add('is-visible'); });
    }

    measureFilm();
    targetProgress = displayedProgress = readFilmProgress();
    applyProgress(displayedProgress);
    updateSystemProgress();
    window.addEventListener('scroll', updateTargets, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
    root.addEventListener('pointermove', handlePointer, { passive: true });

    root.fsMotionCleanup = function () {
      window.clearTimeout(scrollEndTimer);
      if (frame) window.cancelAnimationFrame(frame);
      if (pointerFrame) window.cancelAnimationFrame(pointerFrame);
      if (systemObserver) systemObserver.disconnect();
      if (revealObserver) revealObserver.disconnect();
      window.removeEventListener('scroll', updateTargets);
      window.removeEventListener('resize', handleResize);
      root.removeEventListener('pointermove', handlePointer);
      delete root.fsMotionCleanup;
      delete root.dataset.fsMotionInitialized;
    };
  }

  function initAll(scope) {
    var context = scope || document;
    if (context.matches && context.matches(ROOT_SELECTOR)) initMotion(context);
    Array.prototype.forEach.call(context.querySelectorAll(ROOT_SELECTOR), initMotion);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); }, { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) { initAll(event.target); });
  document.addEventListener('shopify:section:unload', function (event) {
    var root = event.target.matches && event.target.matches(ROOT_SELECTOR) ? event.target : event.target.querySelector(ROOT_SELECTOR);
    if (root && root.fsMotionCleanup) root.fsMotionCleanup();
  });
}());
